import React from 'react';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert } from '@mui/material';
import { CorporateActionTypeLabels, CorporateActionTypeColors } from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);
const SignalChip = ({ signal }) => {
    const colorMap = { BUY: 'success', SELL: 'error', NEUTRAL: 'default', LONG_VOL: 'success', SHORT_VOL: 'error' };
    return <Chip label={signal || '-'} size="small" color={colorMap[signal] || 'default'} />;
};

const DocumentsTab = ({ data, loading, error }) => {
    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return <TabLoading />;

    const corporateActions = data.corporate_actions || [];
    const earnings = data.earnings || [];
    const events = data.events || [];

    return (
        <Box>
            {/* Corporate Actions */}
            <SectionTitle>Corporate Actions</SectionTitle>
            {corporateActions.length === 0 ? <EmptyState message="No corporate actions" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Ex-Date</TableCell>
                                <TableCell>Pay Date</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Description</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {corporateActions.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={CorporateActionTypeLabels[row.action_type] || row.action_type} size="small" color={CorporateActionTypeColors[row.action_type] || 'default'} />
                                    </TableCell>
                                    <TableCell>{formatDate(row.ex_date)}</TableCell>
                                    <TableCell>{formatDate(row.pay_date)}</TableCell>
                                    <TableCell align="right">{row.amount != null ? `$${Number(row.amount).toFixed(4)}` : '-'}</TableCell>
                                    <TableCell>{row.description || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Earnings */}
            <SectionTitle>Earnings</SectionTitle>
            {earnings.length === 0 ? <EmptyState message="No earnings data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Quarter</TableCell>
                                <TableCell>Fiscal Year</TableCell>
                                <TableCell align="right">Est. EPS</TableCell>
                                <TableCell align="right">Actual EPS</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {earnings.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{formatDate(row.earnings_date)}</TableCell>
                                    <TableCell>{row.fiscal_quarter || '-'}</TableCell>
                                    <TableCell>{row.fiscal_year || '-'}</TableCell>
                                    <TableCell align="right">{row.estimated_eps != null ? `$${Number(row.estimated_eps).toFixed(2)}` : '-'}</TableCell>
                                    <TableCell align="right">{row.actual_eps != null ? `$${Number(row.actual_eps).toFixed(2)}` : '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Events */}
            <SectionTitle>Events</SectionTitle>
            {events.length === 0 ? <EmptyState message="No events" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Event</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>AI Preview</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {events.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{row.event_name || '-'}</TableCell>
                                    <TableCell>{row.event_type || '-'}</TableCell>
                                    <TableCell>{formatDate(row.event_date)}</TableCell>
                                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {row.ai_preview ? row.ai_preview.substring(0, 120) + (row.ai_preview.length > 120 ? '...' : '') : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default DocumentsTab;
